import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronDown,
    ChevronUp,
    ListMusic,
    MoreVertical,
    Pause,
    Pencil,
    Play,
    Plus,
    Trash2,
    X,
    Shuffle,
    Repeat,
    ListPlus,
    Heart,
    Download,
    Ellipsis,
    CornerDownRight,
    Info,
} from 'lucide-react';
import { Track } from '../components/PageComponents/trackList/track';
import { Playlist, PlaylistsContext } from '../context/PlaylistsContext';
import { PlayerContext } from '../context/PlayerContext';
import { TrackMenu } from '../components/PageComponents/trackMenu/TrackMenu';
import { TrackInfoModal } from '../components/PageComponents/trackMenu/TrackInfoModal';
import { useTrackMenu } from '../hooks/useTrackMenu';
import { useDetailMenu } from '../hooks/useDetailMenu';
import { useFavourite } from '../hooks/useFavourite';
import { DetailMoreMenu } from '../components/PageComponents/detailMenu/DetailMoreMenu';
import { EntityInfoModal } from '../components/PageComponents/editModal/EntityInfoModal';
import { API_BASE } from '../config/api';
import './Detail.css';
import './Playlists.css';
import './PlaylistDetail.css';

function formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
}

export default function PlaylistDetail(): React.JSX.Element {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getPlaylistWithTracks, setPlaylistTracks, updatePlaylist, deletePlaylist } = useContext(PlaylistsContext);
    const {
        tracks,
        refreshTracks,
        currentSong,
        isPlaying,
        playTrackNow,
        togglePlay,
        setQueue,
        toggleShuffle,
        repeatMode,
        setRepeatMode,
        addTracksToQueue,
        playTracksNext,
    } = useContext(PlayerContext);
    const { menu, openAtCursor, openAtElement, closeMenu, infoTrack, openTrackInfo, closeTrackInfo } = useTrackMenu();
    const { position: moreMenuPosition, openAtElement: openMoreMenu, close: closeMoreMenu } = useDetailMenu();

    const [playlist, setPlaylist] = useState<Playlist | null>(null);
    const [trackIds, setTrackIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    const [showPicker, setShowPicker] = useState(false);
    const [pickerQuery, setPickerQuery] = useState('');

    const { favourited, toggle: toggleFavourite } = useFavourite('playlist', playlist?.id);

    useEffect(() => {
        refreshTracks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!id) return;
        setEditing(false);
        (async () => {
            setLoading(true);
            const data = await getPlaylistWithTracks(Number(id));
            if (data) {
                setPlaylist({ id: data.id, name: data.name, description: data.description, creatorId: data.creatorId });
                setTrackIds(data.trackIds);
                setNotFound(false);
            } else {
                setNotFound(true);
            }
            setLoading(false);
        })();
    }, [id, getPlaylistWithTracks]);

    // Pairs each playlist entry with its resolved Track and its absolute
    // index in trackIds - the index (not the filtered position) is what
    // move/remove operate on, so a track that fails to resolve (e.g. tracks
    // still loading) can't shift everything after it out of alignment.
    const rows = useMemo(() => {
        return trackIds
            .map((trackId, index) => ({ index, track: tracks.find((t) => t.getId() === trackId) }))
            .filter((row): row is { index: number; track: Track } => Boolean(row.track));
    }, [trackIds, tracks]);

    const playlistTracks = useMemo(() => rows.map((r) => r.track), [rows]);

    const persistTracks = async (nextTrackIds: number[]) => {
        setTrackIds(nextTrackIds);
        if (!playlist) return;
        const result = await setPlaylistTracks(playlist.id, nextTrackIds);
        if (result) setTrackIds(result.trackIds);
    };

    const moveBy = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= trackIds.length) return;
        const next = [...trackIds];
        const [moved] = next.splice(index, 1);
        next.splice(target, 0, moved);
        persistTracks(next);
    };

    const removeAt = (index: number) => {
        persistTracks(trackIds.filter((_, i) => i !== index));
    };

    const addTrack = (trackId: number) => {
        persistTracks([...trackIds, trackId]);
    };

    const handlePlayPlaylist = () => {
        const resolved = trackIds
            .map((trackId) => tracks.find((t) => t.getId() === trackId))
            .filter((t): t is Track => Boolean(t));
        if (resolved.length > 0) setQueue(resolved, 0);
    };

    // See albumDetail.tsx's identical handlers for why these start a fresh
    // setQueue rather than toggling shuffle/repeat on whatever's currently
    // playing.
    const handleShuffleAll = () => {
        setQueue(playlistTracks, 0);
        toggleShuffle();
    };

    const handleLoopAll = () => {
        setQueue(playlistTracks, 0);
        setRepeatMode('all');
    };

    const handleTrackClick = (track: Track) => {
        if (currentSong?.getId() === track.getId()) togglePlay();
        else playTrackNow(track);
    };

    const startEditing = () => {
        if (!playlist) return;
        setEditName(playlist.name);
        setEditDescription(playlist.description);
        setEditing(true);
    };

    const handleSaveEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!playlist || !editName.trim()) return;
        setSavingEdit(true);
        const updated = await updatePlaylist(playlist.id, {
            name: editName.trim(),
            description: editDescription.trim(),
        });
        setSavingEdit(false);
        if (updated) {
            setPlaylist(updated);
            setEditing(false);
        }
    };

    const handleDelete = async () => {
        if (!playlist) return;
        if (!window.confirm(`Delete "${playlist.name}"? This can't be undone.`)) return;
        const ok = await deletePlaylist(playlist.id);
        if (ok) navigate('/playlists');
    };

    const normalizedQuery = pickerQuery.trim().toLowerCase();
    const pickerResults = normalizedQuery
        ? tracks.filter(
              (t) =>
                  t.getTitle().toLowerCase().includes(normalizedQuery) ||
                  t.getArtist().toLowerCase().includes(normalizedQuery)
          )
        : tracks;

    if (loading) {
        return (
            <div className="pageContent">
                <div className="trackListEmpty">Loading playlist…</div>
            </div>
        );
    }

    if (notFound || !playlist) {
        return (
            <div className="pageContent">
                <div className="trackListEmpty">Playlist not found</div>
            </div>
        );
    }

    return (
        <div className="pageContent">
            <div className="detailHero">
                <div className="detailHeroArt">
                    <ListMusic size={40} strokeWidth={2.75} />
                </div>
                <div className="detailHeroMeta">
                    <span className="detailHeroKicker">Playlist</span>

                    {editing ? (
                        <form className="newPlaylistForm" onSubmit={handleSaveEdit}>
                            <input
                                className="newPlaylistInput"
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                maxLength={60}
                                autoFocus
                            />
                            <input
                                className="newPlaylistInput"
                                type="text"
                                placeholder="Description (optional)"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                maxLength={255}
                            />
                            <div className="newPlaylistActions">
                                <button type="button" className="newPlaylistCancel" onClick={() => setEditing(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="newPlaylistSubmit"
                                    disabled={!editName.trim() || savingEdit}
                                >
                                    {savingEdit ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <h1 className="detailHeroTitle">{playlist.name}</h1>
                            {playlist.description && <span className="detailHeroSub">{playlist.description}</span>}
                            <span className="detailHeroMuted">
                                {trackIds.length} track{trackIds.length === 1 ? '' : 's'}
                            </span>
                        </>
                    )}

                    {!editing && (
                        <div className="detailHeroActions">
                            {trackIds.length > 0 && (
                                <>
                                    <button className="detailPlayButton" onClick={handlePlayPlaylist}>
                                        <Play size={16} strokeWidth={2.75} fill="currentColor" />
                                        Play
                                    </button>
                                    <button className="detailPillButton" onClick={handleShuffleAll}>
                                        <Shuffle size={15} strokeWidth={2.75} />
                                        Shuffle
                                    </button>
                                    <button
                                        className={`detailPillButton ${repeatMode !== 'off' ? 'detailPillButtonActive' : ''}`}
                                        onClick={handleLoopAll}
                                    >
                                        <Repeat size={15} strokeWidth={2.75} />
                                        Repeat
                                    </button>
                                    <button className="detailPillButton" onClick={() => addTracksToQueue(playlistTracks)}>
                                        <ListPlus size={15} strokeWidth={2.75} />
                                        Queue
                                    </button>
                                </>
                            )}
                            <button
                                className={`detailIconButton ${favourited ? 'detailIconButtonActive' : ''}`}
                                onClick={toggleFavourite}
                                aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
                            >
                                <Heart size={16} strokeWidth={2.75} fill={favourited ? 'currentColor' : 'none'} />
                            </button>
                            <button className="detailIconButton" onClick={startEditing} aria-label="Edit playlist">
                                <Pencil size={16} strokeWidth={2.75} />
                            </button>
                            <a
                                className="detailIconButton"
                                href={`${API_BASE}/api/download?type=playlist&id=${playlist.id}`}
                                download
                                aria-label="Download playlist"
                            >
                                <Download size={16} strokeWidth={2.75} />
                            </a>
                            <button className="detailIconButton" onClick={openMoreMenu} aria-label="More options">
                                <Ellipsis size={16} strokeWidth={2.75} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="playlistTrackList">
                {rows.length === 0 ? (
                    <div className="trackListEmpty">No tracks yet - add some below</div>
                ) : (
                    rows.map(({ index, track }) => {
                        const isThisTrackPlaying = currentSong?.getId() === track.getId() && isPlaying;
                        return (
                            <div
                                key={`${track.getId()}-${index}`}
                                className={`playlistTrackRow ${
                                    currentSong?.getId() === track.getId() ? 'playlistTrackRowActive' : ''
                                }`}
                                onContextMenu={(e) => openAtCursor(e, track, index)}
                            >
                                <span className="playlistTrackPlayIcon" onClick={() => handleTrackClick(track)}>
                                    {isThisTrackPlaying ? (
                                        <Pause size={14} strokeWidth={2.75} fill="currentColor" />
                                    ) : (
                                        <Play size={14} strokeWidth={2.75} fill="currentColor" />
                                    )}
                                </span>
                                <span className="playlistTrackMeta" onClick={() => handleTrackClick(track)}>
                                    <span className="playlistTrackTitle">{track.getTitle()}</span>
                                    <span className="playlistTrackArtist">{track.getArtist()}</span>
                                </span>
                                <span className="detailTrackDuration">{formatDuration(track.getDuration())}</span>
                                <span className="playlistTrackActions">
                                    <button
                                        className="playlistTrackIconButton"
                                        onClick={() => moveBy(index, -1)}
                                        disabled={index === 0}
                                        aria-label="Move up"
                                    >
                                        <ChevronUp size={14} strokeWidth={2.75} />
                                    </button>
                                    <button
                                        className="playlistTrackIconButton"
                                        onClick={() => moveBy(index, 1)}
                                        disabled={index === trackIds.length - 1}
                                        aria-label="Move down"
                                    >
                                        <ChevronDown size={14} strokeWidth={2.75} />
                                    </button>
                                    <button
                                        className="playlistTrackIconButton"
                                        onClick={() => removeAt(index)}
                                        aria-label="Remove from playlist"
                                    >
                                        <X size={14} strokeWidth={2.75} />
                                    </button>
                                    <button
                                        className="playlistTrackIconButton"
                                        onClick={(e) => openAtElement(e, track, index)}
                                        aria-label={`More options for ${track.getTitle()}`}
                                    >
                                        <MoreVertical size={14} strokeWidth={2.75} />
                                    </button>
                                </span>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="playlistSection">
                <h2 className="playlistSectionTitle">Add tracks</h2>
                {showPicker ? (
                    <>
                        <input
                            className="playlistPickerSearch"
                            type="text"
                            placeholder="Search your library…"
                            value={pickerQuery}
                            onChange={(e) => setPickerQuery(e.target.value)}
                            autoFocus
                        />
                        <div className="playlistPickerList">
                            {pickerResults.length === 0 ? (
                                <div className="trackListEmpty">No tracks match your search</div>
                            ) : (
                                pickerResults.map((track) => (
                                    <div
                                        key={track.getId()}
                                        className="playlistPickerRow"
                                        onClick={() => addTrack(track.getId())}
                                    >
                                        <span className="playlistPickerMeta">
                                            <span className="playlistPickerTitle">{track.getTitle()}</span>
                                            <span className="playlistPickerArtist">{track.getArtist()}</span>
                                        </span>
                                        <button
                                            className="playlistPickerAddButton"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                addTrack(track.getId());
                                            }}
                                            aria-label={`Add ${track.getTitle()}`}
                                        >
                                            <Plus size={14} strokeWidth={2.75} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                ) : (
                    <button className="playlistHeroIconButton" onClick={() => setShowPicker(true)}>
                        <Plus size={14} strokeWidth={2.75} />
                        Add tracks
                    </button>
                )}
            </div>

            {menu && (
                <TrackMenu
                    track={menu.track}
                    x={menu.x}
                    y={menu.y}
                    onClose={closeMenu}
                    currentPlaylistId={playlist.id}
                    onRemoveFromCurrentPlaylist={
                        menu.playlistItemIndex !== undefined ? () => removeAt(menu.playlistItemIndex!) : undefined
                    }
                    onShowInfo={openTrackInfo}
                />
            )}
            {infoTrack && <TrackInfoModal track={infoTrack} onClose={closeTrackInfo} />}

            {showInfo && (
                <EntityInfoModal
                    title="Playlist info"
                    rows={[
                        ['Name', playlist.name],
                        ['Description', playlist.description],
                        ['Tracks', String(trackIds.length)],
                    ]}
                    onClose={() => setShowInfo(false)}
                />
            )}
            {moreMenuPosition && (
                <DetailMoreMenu
                    x={moreMenuPosition.x}
                    y={moreMenuPosition.y}
                    onClose={closeMoreMenu}
                    tracks={playlistTracks}
                    itemsBefore={[
                        { label: 'Play now', icon: Play, onClick: handlePlayPlaylist },
                        { label: 'Play next', icon: CornerDownRight, onClick: () => playTracksNext(playlistTracks) },
                        { label: 'Add to queue', icon: ListPlus, onClick: () => addTracksToQueue(playlistTracks) },
                    ]}
                    itemsAfter={[
                        {
                            label: 'Download',
                            icon: Download,
                            onClick: () => window.location.assign(`${API_BASE}/api/download?type=playlist&id=${playlist.id}`),
                        },
                        { label: 'Playlist info', icon: Info, onClick: () => setShowInfo(true) },
                        { label: 'Delete playlist', icon: Trash2, onClick: handleDelete, danger: true },
                    ]}
                />
            )}
        </div>
    );
}
